import { type ReactElement, useState } from 'react'
import { useNavigate } from 'react-router'
import { InsufficientPhotosPopup } from 'pages/PhotoBookPage/InsufficientPhotosPopup'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { getCollageProperties } from 'services/Analytics/photoAnalytics/photoAnalytics'
import { useTilesStore } from 'stores/RootStoreProvider'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { AiTileGeneratingPopup } from '../components/AiTileGeneratingPopup'
import { IncompleteCollagePopup } from '../components/IncompleteCollagePopup'
import { TooManyPagesPopup } from '../components/TooManyPagesPopup'
import { getEditRoute } from '../cart.utils'
import { type CheckoutBlocker, useCheckoutBlocker } from './useCheckoutBlocker'

export function useCheckoutBlockerPopup({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const tilesStore = useTilesStore()
  const checkoutBlocker = useCheckoutBlocker()
  const [isOpen, setIsOpen] = useState(false)

  if (isOpen && !checkoutBlocker) {
    setIsOpen(false)
  }

  const trackIncompleteCollageEvent = (eventName: string, tileId: string) => {
    const collageProperties = readOutsideReaction(() =>
      getCollageProperties(tilesStore.tiles.find((tile) => tile.id === tileId))
    )
    analytics.track(eventName, collageProperties)
  }

  const openPopupIfBlocked = () => {
    if (!checkoutBlocker) {
      return false
    }
    setIsOpen(true)
    if (checkoutBlocker.type === 'incomplete-collage') {
      trackIncompleteCollageEvent(
        EVENT_NAMES.INCOMPLETE_COLLAGE_POPUP_OPENED,
        checkoutBlocker.warning.tileId
      )
    }
    return true
  }

  const fixCheckoutBlocker = ({ item, warning }: CheckoutBlocker) => {
    const route = getEditRoute(item)
    if (!route) {
      throw new Error(`No editor to fix a ${item.productType} checkout blocker`)
    }
    setIsOpen(false)
    onClose()
    navigate(route, { state: { warningResolve: warning } })
  }

  const renderPopup = (blocker: CheckoutBlocker): ReactElement => {
    switch (blocker.type) {
      case 'incomplete-collage':
        return (
          <IncompleteCollagePopup
            open={isOpen}
            warning={blocker.warning}
            onDismiss={() => {
              setIsOpen(false)
              trackIncompleteCollageEvent(
                EVENT_NAMES.INCOMPLETE_COLLAGE_POPUP_DISMISSED,
                blocker.warning.tileId
              )
            }}
            onApprove={() => {
              trackIncompleteCollageEvent(
                EVENT_NAMES.INCOMPLETE_COLLAGE_POPUP_APPROVED,
                blocker.warning.tileId
              )
              fixCheckoutBlocker(blocker)
            }}
          />
        )
      case 'ai-tile-generating':
        return (
          <AiTileGeneratingPopup
            open={isOpen}
            warning={blocker.warning}
            onDismiss={() => setIsOpen(false)}
          />
        )
      case 'not-enough-photos':
        return (
          <InsufficientPhotosPopup
            open={isOpen}
            screen="cart"
            book={blocker.item}
            onDismiss={() => setIsOpen(false)}
            onApprove={() => fixCheckoutBlocker(blocker)}
          />
        )
      case 'too-many-pages':
        return (
          <TooManyPagesPopup
            open={isOpen}
            book={blocker.item}
            onDismiss={() => setIsOpen(false)}
            onEditBookTap={() => fixCheckoutBlocker(blocker)}
          />
        )
    }
  }

  const popup = checkoutBlocker ? renderPopup(checkoutBlocker) : null

  return { popup, openPopupIfBlocked }
}
